import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { ArrowRight, Users, Server, Shield, Cloud, HelpCircle, Wrench } from 'lucide-react';
import { usePageContent } from '@/hooks/usePageContent';
import EditableText from '@/components/EditableText';
import TrustBadges from '@/components/TrustBadges';

const PricingCalculator = () => {
  const [users, setUsers] = useState(10);
  const [servers, setServers] = useState(0);
  const [addOns, setAddOns] = useState({
    security: false,
    cloud: false,
    compliance: false,
    onsite: false,
  });

  const [monthlyPrice, setMonthlyPrice] = useState(0);

  useEffect(() => {
    // Base pricing calculation
    let base = users * 125; // $125 per user base
    base += servers * 200; // $200 per server

    // Add-ons
    if (addOns.security) base += users * 25;
    if (addOns.cloud) base += users * 15;
    if (addOns.compliance) base += 500;
    if (addOns.onsite) base += 800;

    setMonthlyPrice(base);
  }, [users, servers, addOns]);

  const scrollToContact = () => {
    window.location.href = '/contact-us';
  };

  return (
    <section id="calculator" className="py-24 sm:py-32 bg-[#0A0A0A]" data-testid="pricing-calculator">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <EditableText pageType="home" pageId="home" field="pricing.badge" as="span" className="text-xs tracking-[0.2em] uppercase font-bold text-[#E63946] mb-4 block">
            Transparent Pricing
          </EditableText>
          <EditableText pageType="home" pageId="home" field="pricing.heading" as="h2" richText className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4">
            Calculate Your Investment
          </EditableText>
          <EditableText pageType="home" pageId="home" field="pricing.subheading" as="p" richText className="text-gray-400 max-w-2xl mx-auto">
            No hidden fees. No surprises. See exactly what it costs to protect your business.
          </EditableText>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            {/* Users Slider */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#E63946]" />
                  <span className="text-white font-medium">Number of Users</span>
                </div>
                <span className="text-2xl font-bold text-white">{users}</span>
              </div>
              <Slider
                value={[users]}
                onValueChange={(value) => setUsers(value[0])}
                min={5}
                max={200}
                step={5}
                className="w-full"
                aria-label="Number of users"
                data-testid="calculator-users-slider"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>5</span>
                <span>200+</span>
              </div>
            </div>

            {/* Servers Slider */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-[#E63946]" />
                  <span className="text-white font-medium">Servers / VMs</span>
                </div>
                <span className="text-2xl font-bold text-white">{servers}</span>
              </div>
              <Slider
                value={[servers]}
                onValueChange={(value) => setServers(value[0])}
                min={0}
                max={20}
                step={1}
                className="w-full"
                aria-label="Number of servers"
                data-testid="calculator-servers-slider"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0</span>
                <span>20+</span>
              </div>
            </div>

            {/* Add-ons */}
            <div className="space-y-6">
              <h3 className="text-white font-medium">Add-On Services</h3>

              {/* Security Add-on */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <span className="text-white block">Advanced Security</span>
                    <span className="text-xs text-gray-500">EDR, SIEM, Dark Web Monitoring</span>
                  </div>
                </div>
                <Switch
                  checked={addOns.security}
                  onCheckedChange={(checked) => setAddOns({ ...addOns, security: checked })}
                  aria-label="Toggle Advanced Security"
                  data-testid="calculator-security-toggle"
                />
              </div>

              {/* Cloud Add-on */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <Cloud className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <span className="text-white block">Cloud Management</span>
                    <span className="text-xs text-gray-500">Azure/AWS, M365 Premium Support</span>
                  </div>
                </div>
                <Switch
                  checked={addOns.cloud}
                  onCheckedChange={(checked) => setAddOns({ ...addOns, cloud: checked })}
                  aria-label="Toggle Cloud Management"
                  data-testid="calculator-cloud-toggle"
                />
              </div>

              {/* Compliance Add-on */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <span className="text-white block">Compliance Package</span>
                    <span className="text-xs text-gray-500">HIPAA, SOC 2, PCI DSS Support</span>
                  </div>
                </div>
                <Switch
                  checked={addOns.compliance}
                  onCheckedChange={(checked) => setAddOns({ ...addOns, compliance: checked })}
                  aria-label="Toggle Compliance Package"
                  data-testid="calculator-compliance-toggle"
                />
              </div>

              {/* Same Day Onsite Support Add-on */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <span className="text-white block">Same Day Onsite Support</span>
                    <span className="text-xs text-gray-500">8 hours of onsite support</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-600 line-through">$1,200</span>
                      <span className="text-xs font-semibold text-[#E63946]">$800/mo</span>
                      <span className="text-[10px] bg-[#E63946]/15 text-[#E63946] px-1.5 py-0.5 font-medium">SAVE 33%</span>
                    </div>
                  </div>
                </div>
                <Switch
                  checked={addOns.onsite}
                  onCheckedChange={(checked) => setAddOns({ ...addOns, onsite: checked })}
                  aria-label="Toggle Same Day Onsite Support"
                  data-testid="calculator-onsite-toggle"
                />
              </div>
            </div>
          </motion.div>

          {/* Price Display */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="calculator-display p-8 md:p-12 sticky top-24" data-testid="calculator-display">
              <div className="relative z-10">
                <div className="text-sm text-gray-400 mb-2">Estimated Monthly Investment</div>
                <div className="text-5xl md:text-6xl font-bold text-white mb-2 stat-counter">
                  ${monthlyPrice.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500 mb-8">
                  ~${Math.round(monthlyPrice / users)}/user/month
                </div>

                {/* Breakdown */}
                <div className="space-y-3 mb-8 border-t border-white/10 pt-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Base IT Management</span>
                    <span className="text-white">${(users * 125).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Server Management</span>
                    <span className="text-white">${(servers * 200).toLocaleString()}</span>
                  </div>
                  {addOns.security && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Advanced Security</span>
                      <span className="text-white">${(users * 25).toLocaleString()}</span>
                    </div>
                  )}
                  {addOns.cloud && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Cloud Management</span>
                      <span className="text-white">${(users * 15).toLocaleString()}</span>
                    </div>
                  )}
                  {addOns.compliance && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Compliance Package</span>
                      <span className="text-white">$500</span>
                    </div>
                  )}
                  {addOns.onsite && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Same Day Onsite Support</span>
                      <span className="text-white">$800</span>
                    </div>
                  )}
                </div>

                <Button
                  onClick={scrollToContact}
                  className="w-full btn-primary-new py-6 rounded-none text-base"
                  data-testid="calculator-cta"
                >
                  Get Custom Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  Final pricing based on your specific requirements
                </p>

                {/* Trust signal under the price/CTA — reduces "is this MSP legit?" objection. */}
                <div className="mt-6 pt-6 border-t border-white/5 flex justify-center">
                  <TrustBadges variant="centered" size="sm" showTagline tagline="BBB Accredited · Proudly Canadian" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingCalculator;
